import React, { useEffect, useMemo, useState, useCallback } from "react";
import { usePersistedState } from "@/lib/usePersistedState";
import { useSearchParams } from "react-router-dom";
import { AnomalyAlerts } from "@/components/AnomalyAlerts";
import { DatePicker, fmtDate } from "@/components/DatePicker";
import api, { usd, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { StatusBadge } from "@/components/RoleBadge";
import { OvertimeBanner } from "@/components/OvertimeBanner";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaystubMessagePicker } from "@/components/PaystubMessagePicker";
import { downloadPdf } from "@/components/HRPhase3Tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, Play, CheckCircle2, ChevronRight, ChevronDown, Loader2, Printer, CalendarClock, FileText, FileDown } from "lucide-react";
import { PaymentSplitCard } from "@/components/PaymentSplitCard";
import { toast } from "sonner";
import { AfterTheFactDialog, ManualCheckDialog, DeleteRunDialog } from "@/components/PayrollExtras";
import { DeductionCodes } from "@/components/DeductionCodes";
import { DeductionReportDialog } from "@/components/DeductionReportDialog";
import { TipsApprovalCard } from "@/components/TipReport";
import { EarningsStep } from "@/components/EarningsStep";
import { History, FileCheck2, Trash2, Tags } from "lucide-react";
import { format } from "date-fns";

function today(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function PayrollRuns() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const [runs, setRuns] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [atfOpen, setAtfOpen] = useState(false);
  const [mcOpen, setMcOpen] = useState(false);
  const [delRun, setDelRun] = useState(null);
  const [dedRun, setDedRun] = useState(null);
  const [empList, setEmpList] = useState([]);
  useEffect(() => { api.get("/employees", { params: params() }).then((r) => setEmpList(r.data.filter((e) => e.worker_type !== "contractor"))).catch(() => {}); }, [activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dates, setDates] = useState({ period_start: today(-14), period_end: today(-1), pay_date: today(2) });
  const [paystubNote, setPaystubNote] = useState("");
  const [openMonths, setOpenMonths] = usePersistedState("payroll-months", null);
  const years = useMemo(() => [...new Set(runs.map((r) => (r.pay_date || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [runs]);
  const [year, setYear] = usePersistedState("payroll-year", null);
  const activeYear = year && years.includes(year) ? year : years[0];
  const yearTotals = useMemo(() => { const rs = runs.filter((r) => (r.pay_date || "").startsWith(activeYear || "")); return { runs: rs.length, gross: rs.reduce((a, r) => a + (r.gross_total || 0), 0), tax: rs.reduce((a, r) => a + (r.tax_total || 0), 0), net: rs.reduce((a, r) => a + (r.net_total || 0), 0) }; }, [runs, activeYear]);
  const months = useMemo(() => {
    const g = {};
    runs.filter((r) => !activeYear || (r.pay_date || "").startsWith(activeYear)).forEach((r) => { const k = (r.pay_date || "").slice(0, 7); (g[k] = g[k] || []).push(r); });
    return Object.keys(g).sort().reverse().map((k) => ({ key: k, label: new Date(`${k}-15T00:00:00`).toLocaleString("en-US", { month: "long", year: "numeric" }), runs: g[k], gross: g[k].reduce((a, r) => a + (r.gross_total || 0), 0), tax: g[k].reduce((a, r) => a + (r.tax_total || 0), 0), net: g[k].reduce((a, r) => a + (r.net_total || 0), 0), pending: g[k].filter((r) => r.status === "pending_approval").length }));
  }, [runs, activeYear]);
  const isOpen = (m, i) => openMonths ? !!openMonths[m.key] : (i === 0 || m.pending > 0);
  const toggleMonth = (m) => setOpenMonths((o) => { const cur = o || Object.fromEntries(months.map((x, i) => [x.key, isOpen(x, i)])); return { ...cur, [m.key]: !cur[m.key] }; });

  const params = () => (user.role === "bureau_admin" ? { company_id: activeCompanyId } : {});

  const load = useCallback(() => {
    if (user.role === "bureau_admin" && !activeCompanyId) return;
    api.get("/payroll/runs", { params: params() }).then((r) => setRuns(r.data)).catch(() => {});
  }, [user.role, activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const rid = searchParams.get("run");
    if (rid && runs.some((r) => r.id === rid)) { openDetail({ id: rid }); setSearchParams({}, { replace: true }); }
  }, [runs]); // eslint-disable-line react-hooks/exhaustive-deps

  const decideRun = async (id, action) => {
    const note = action === "reject" ? window.prompt("Reason for rejecting this run:") ?? "" : "";
    if (action === "reject" && !window.confirm("Reject this run? Paystubs will be discarded and it can be re-run.")) return;
    try { await api.post(`/payroll/runs/${id}/${action}`, { note }); toast.success(action === "approve" ? "Payroll approved — paystubs released and emailed" : "Run rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [overrides, setOverrides] = useState({});
  const refreshPreview = async (ov = overrides, d = dates) => {
    setLoading(true);
    try { const { data } = await api.post("/payroll/preview", { ...params(), period_start: d.period_start, period_end: d.period_end, overrides: ov }); setPreview(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const setLineInput = (eid, patch) => {
    const line = preview.lines.find((l) => l.employee_id === eid);
    const cur = overrides[eid] || { rate_hours: line.inputs.rate_hours, overtime_hours: line.inputs.overtime_hours, tips_cash: line.inputs.tips_cash, tips_paycheck: line.inputs.tips_paycheck };
    const ov = { ...overrides, [eid]: { ...cur, ...patch } };
    setOverrides(ov);
    clearTimeout(window.__rpPreviewT); window.__rpPreviewT = setTimeout(() => refreshPreview(ov), 500);
  };

  const startWizard = async () => {
    setStep(0); setPreview(null); setOverrides({}); setWizardOpen(true); setLoading(true);
    try {
      const { data } = await api.post("/payroll/preview", params());
      setPreview(data);
      if (data.schedule?.next) setDates(data.schedule.next);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setWizardOpen(false); }
    finally { setLoading(false); }
  };

  const execute = async () => {
    setLoading(true);
    try {
      await api.post("/payroll/runs", { ...params(), ...dates, overrides, paystub_note: paystubNote });
      toast.success("Payroll run completed");
      setWizardOpen(false); setPaystubNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openDetail = async (run) => {
    const { data } = await api.get(`/payroll/runs/${run.id}`);
    setDetail(data);
  };
  const [printing, setPrinting] = useState(false);
  const [pkgBusy, setPkgBusy] = useState(false);
  const downloadPackage = async (run) => {
    setPkgBusy(true);
    try {
      const res = await api.get(`/reports/package/${run.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `Payroll_Package_${fmtDate(run.pay_date).replace(/\//g, "-")}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Payroll package downloaded");
    } catch { toast.error("Could not generate payroll package"); } finally { setPkgBusy(false); }
  };
  const printChecks = async (run, reprint) => {
    if (reprint && !window.confirm("Reprint will void the previous numbers and assign new check numbers. Continue?")) return;
    setPrinting(true);
    try {
      const res = await api.post(`/check-setup/print/${run.id}`, { reprint }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `Checks_${fmtDate(run.pay_date).replace(/\//g, "-")}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Checks ${res.headers["x-check-range"] || ""} generated`); openDetail(run);
    } catch (e) {
      let msg = "Could not generate checks";
      try { msg = formatApiErrorDetail(JSON.parse(await e.response?.data?.text())?.detail); } catch { /* blob not json */ }
      toast.error(msg);
    } finally { setPrinting(false); }
  };

  const STEPS = ["Period & Employees", "Gross Pay Review", "Deductions & Taxes", "Submit & Execute"];

  return (
    <div className="space-y-6 animate-fade-up">
      <OvertimeBanner companyId={user.role === "bureau_admin" ? activeCompanyId : user.company_id} isAdmin={user.role === "bureau_admin"} />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Payroll Runs</h1>
          <p className="text-sm text-muted-foreground mt-1">Execute and review pay batches.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="atf-button" onClick={() => setAtfOpen(true)}><History className="w-4 h-4 mr-1.5" /> After-the-fact</Button>
          <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="manual-check-button" onClick={() => setMcOpen(true)}><FileCheck2 className="w-4 h-4 mr-1.5" /> Manual check</Button>
          <Button data-testid="run-payroll-button" onClick={startWizard}><Play className="w-4 h-4 mr-1.5" /> Run Payroll</Button>
        </div>
      </div>
      <AfterTheFactDialog open={atfOpen} onOpenChange={setAtfOpen} params={params} employees={empList} onDone={load} />
      <ManualCheckDialog open={mcOpen} onOpenChange={setMcOpen} params={params} employees={empList} onDone={load} />
      <DeleteRunDialog run={delRun} onOpenChange={(o) => !o && setDelRun(null)} onDone={load} />
      <DeductionReportDialog run={dedRun} onOpenChange={(o) => !o && setDedRun(null)} />

      {years.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="year-filter">
          <div className="flex flex-wrap gap-1.5">{years.map((y) => <button key={y} type="button" data-testid={`year-${y}`} onClick={() => { setYear(y); setOpenMonths(null); }} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${y === activeYear ? "bg-[#0b1a3a] text-white border-[#0b1a3a]" : "hover:bg-accent"}`}>{y}</button>)}</div>
          <div className="flex items-center gap-3 flex-wrap"><div className="text-xs text-muted-foreground font-mono-num flex flex-wrap gap-x-4" data-testid="year-totals"><span>{yearTotals.runs} run{yearTotals.runs !== 1 ? "s" : ""} in {activeYear}</span><span>Gross <b className="text-foreground">{usd(yearTotals.gross)}</b></span><span>Taxes <b className="text-amber-600">{usd(yearTotals.tax)}</b></span><span>Net <b className="text-emerald-600">{usd(yearTotals.net)}</b></span></div>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="year-summary-pdf" onClick={() => downloadPdf(`/payroll/year-summary.pdf?year=${activeYear}&${new URLSearchParams(params()).toString()}`, `${activeYear}_Payroll_Summary.pdf`)}><FileDown className="w-4 h-4 mr-1.5" /> {activeYear} summary PDF</Button></div>
        </div>
      )}
      {/* Mobile run cards */}
      <div className="lg:hidden space-y-2" data-testid="run-cards">
        {months.map((m, i) => (<div key={m.key} className="space-y-2">
            <button type="button" data-testid={`m-month-${m.key}`} className="w-full flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2 text-sm font-semibold" onClick={() => toggleMonth(m)}><span className="inline-flex items-center gap-2">{isOpen(m, i) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{m.label} <span className="text-xs font-normal text-muted-foreground">{m.runs.length} run{m.runs.length !== 1 ? "s" : ""}</span></span><span className="font-mono-num text-emerald-600">{usd(m.net)}</span></button>
            {isOpen(m, i) && m.runs.map((r) => (
          <Card key={r.id} data-testid={`run-card-${r.id}`} className={`p-3 ${r.status === "pending_approval" ? "border-amber-300 bg-amber-50/40 dark:bg-amber-500/5" : ""}`} onClick={() => openDetail(r)}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0"><div className="font-mono-num font-semibold">Pay {fmtDate(r.pay_date)}</div><div className="text-xs text-muted-foreground font-mono-num">{fmtDate(r.period_start)} → {fmtDate(r.period_end)} · {r.employee_count} paid</div></div>
              <StatusBadge status={r.status} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
              <div><div className="text-muted-foreground">Gross</div><div className="font-mono-num">{usd(r.gross_total)}</div></div>
              <div><div className="text-muted-foreground">Taxes</div><div className="font-mono-num text-amber-600">{usd(r.tax_total)}</div></div>
              <div><div className="text-muted-foreground">Net</div><div className="font-mono-num font-semibold text-emerald-600">{usd(r.net_total)}</div></div>
            </div>
            {r.status === "pending_approval" && user.role === "bureau_admin" && (
              <div className="grid grid-cols-2 gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                <Button className="h-11 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]" data-testid={`m-approve-run-${r.id}`} onClick={() => decideRun(r.id, "approve")}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve</Button>
                <Button variant="outline" className="h-11 text-rose-600 border-rose-300 active:scale-[0.98]" data-testid={`m-reject-run-${r.id}`} onClick={() => decideRun(r.id, "reject")}>Reject</Button>
              </div>)}
          </Card>))}
        </div>))}
        {runs.length === 0 && <Card className="p-8 text-center text-muted-foreground">No payroll runs yet.</Card>}
      </div>

      <Card className="p-0 overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pay Period</TableHead>
                <TableHead>Pay Date</TableHead>
                <TableHead className="text-right">Employees</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Taxes</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m, i) => (<React.Fragment key={m.key}>
                <TableRow data-testid={`month-row-${m.key}`} className="cursor-pointer bg-muted/50 hover:bg-muted" onClick={() => toggleMonth(m)}>
                  <TableCell className="font-semibold" colSpan={2}><span className="inline-flex items-center gap-2">{isOpen(m, i) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{m.label}<span className="text-xs font-normal text-muted-foreground">{m.runs.length} run{m.runs.length !== 1 ? "s" : ""}</span>{m.pending > 0 && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">{m.pending} pending</span>}</span></TableCell>
                  <TableCell className="text-right font-mono-num text-muted-foreground">{m.runs.reduce((a, r) => a + (r.employee_count || 0), 0)}</TableCell>
                  <TableCell className="text-right font-mono-num">{usd(m.gross)}</TableCell>
                  <TableCell className="text-right font-mono-num text-amber-600">{usd(m.tax)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-emerald-600">{usd(m.net)}</TableCell>
                  <TableCell colSpan={2} className="text-xs text-muted-foreground">{isOpen(m, i) ? "Click to collapse" : "Click to expand"}</TableCell>
                </TableRow>
              {isOpen(m, i) && m.runs.map((r) => (
                <TableRow key={r.id} data-testid={`run-row-${r.id}`} className="cursor-pointer" onClick={() => openDetail(r)}>
                  <TableCell className="font-mono-num text-xs">{fmtDate(r.period_start)} → {fmtDate(r.period_end)}</TableCell>
                  <TableCell className="font-mono-num">{fmtDate(r.pay_date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{r.employee_count}</TableCell>
                  <TableCell className="text-right font-mono-num">{usd(r.gross_total)}</TableCell>
                  <TableCell className="text-right font-mono-num text-amber-600">{usd(r.tax_total)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-emerald-600">{usd(r.net_total)}</TableCell>
                  <TableCell><StatusBadge status={r.status} />{r.run_type && <span className="ml-1 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`run-type-${r.id}`}>{r.run_type.replace(/_/g, " ")}</span>}{(r.status !== "completed" || user.role === "bureau_admin") && <>{r.status === "completed" && <Button size="icon" variant="ghost" className="h-6 w-6 ml-1" title="Deduction report" data-testid={`ded-report-${r.id}`} onClick={(e) => { e.stopPropagation(); setDedRun(r); }}><Tags className="w-3.5 h-3.5" /></Button>}<Button size="icon" variant="ghost" className="h-6 w-6 ml-1 text-destructive" title="Delete payroll" data-testid={`delete-run-${r.id}`} onClick={(e) => { e.stopPropagation(); setDelRun(r); }}><Trash2 className="w-3.5 h-3.5" /></Button></>}{r.status === "pending_approval" && user.role === "bureau_admin" && (
                    <span className="ml-2 inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-run-${r.id}`} onClick={() => decideRun(r.id, "approve")}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-rose-600" data-testid={`reject-run-${r.id}`} onClick={() => decideRun(r.id, "reject")}>Reject</Button>
                    </span>)}</TableCell>
                  <TableCell><ChevronRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
              </React.Fragment>))}
              {runs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No payroll runs yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DeductionCodes params={params} />
      <TipsApprovalCard params={params} />

      {/* Wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent data-testid="payroll-run-wizard" className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Run Payroll</DialogTitle></DialogHeader>

          <div className="flex items-center gap-1 mb-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div className={`flex items-center gap-2 text-xs font-medium ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                  <span className={`grid place-items-center w-6 h-6 rounded-full text-[11px] ${i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-primary/15 text-primary border border-primary" : "bg-muted"}`}>
                    {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="hidden sm:block">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-primary" : "bg-border"}`} />}
              </div>
            ))}
          </div>

          {loading && !preview ? (
            <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : preview ? (
            <div className="max-h-[50vh] overflow-y-auto">
              {step === 0 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="space-y-1.5"><Label>Period start</Label><DatePicker testid="period-start" value={dates.period_start} onChange={(v) => setDates({ ...dates, period_start: v })} /></div>
                    <div className="space-y-1.5"><Label>Period end</Label><DatePicker testid="period-end" value={dates.period_end} onChange={(v) => setDates({ ...dates, period_end: v })} /></div>
                    <div className="space-y-1.5"><Label>Pay date</Label><DatePicker testid="pay-date" value={dates.pay_date} onChange={(v) => setDates({ ...dates, pay_date: v })} /></div>
                  </div>
                  <Card className="p-4 bg-muted/40 space-y-2" data-testid="auto-period-card">
                    <p className="text-sm"><strong>{preview.employee_count}</strong> active employees on a <strong className="capitalize">{preview.pay_schedule}</strong> schedule will be included.</p>
                    {preview.schedule && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />
                        Dates auto-filled from the {preview.schedule.pay_schedule} schedule{preview.schedule.based_on_last_run ? ` (last run ended ${fmtDate(preview.schedule.last_period_end)})` : ""}; pay date is {preview.schedule.pay_lag_days} days after period end, skipping weekends. Edit if needed.
                        {JSON.stringify(dates) !== JSON.stringify(preview.schedule.next) && <button type="button" data-testid="reset-auto-dates" className="text-primary underline ml-1" onClick={() => setDates(preview.schedule.next)}>Reset</button>}
                      </p>
                    )}
                    {preview.schedule?.upcoming?.length > 1 && (
                      <div className="text-xs">
                        <div className="text-muted-foreground mb-1">Upcoming periods</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 font-mono-num" data-testid="upcoming-periods">
                          {preview.schedule.upcoming.slice(1).map((p) => <button type="button" key={p.period_start} onClick={() => setDates(p)} className="text-left rounded border bg-background px-2 py-1 hover:bg-accent">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}<div className="text-muted-foreground">pay {fmtDate(p.pay_date)}</div></button>)}
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              )}
              {step === 1 && (
                <EarningsStep preview={preview} overrides={overrides} onChange={setLineInput} loading={loading} />
              )}
              {step === 2 && (
                <Table>
                  <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead className="text-right">Federal</TableHead><TableHead className="text-right">State</TableHead><TableHead className="text-right">FICA</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                  <TableBody>{preview.lines.map((l) => (<TableRow key={l.employee_id}><TableCell>{l.employee_name}</TableCell><TableCell className="text-right font-mono-num">{usd(l.federal_tax)}</TableCell><TableCell className="text-right font-mono-num">{usd(l.state_tax)}</TableCell><TableCell className="text-right font-mono-num">{usd(l.social_security + l.medicare)}</TableCell><TableCell className="text-right font-mono-num font-semibold text-emerald-600">{usd(l.net_pay)}</TableCell></TableRow>))}</TableBody>
                </Table>
              )}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <StatCard label="Total Gross" value={usd(preview.gross_total)} icon={Wallet} />
                    <StatCard label="Total Taxes" value={usd(preview.tax_total)} accent="text-amber-600" />
                    <StatCard label="Total Net Pay" value={usd(preview.net_total)} accent="text-emerald-600" />
                  </div>
                  <PaystubMessagePicker params={params} value={paystubNote} onChange={setPaystubNote} />
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="flex sm:justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Back</Button>
            {step < 3 ? (
              <Button data-testid="wizard-next" onClick={() => { if (step === 0 && (preview.period_start !== dates.period_start || preview.period_end !== dates.period_end)) refreshPreview(overrides, dates); setStep((s) => s + 1); }} disabled={!preview}>Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button data-testid="wizard-execute" onClick={execute} disabled={loading}>{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Execute Payroll</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="run-detail-modal" className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Payroll Run · {fmtDate(detail?.pay_date)}</DialogTitle></DialogHeader>
          {detail && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-2">
                <StatCard label="Gross" value={usd(detail.gross_total)} />
                <StatCard label="Taxes" value={usd(detail.tax_total)} accent="text-amber-600" />
                <StatCard label="Net" value={usd(detail.net_total)} accent="text-emerald-600" />
              </div>
              {detail.status === "pending_approval" && <div className="mb-2"><AnomalyAlerts runId={detail.id} compact /></div>}
              {!["pending_approval", "rejected"].includes(detail.status) && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 rounded-lg border bg-muted/40 px-3 py-2 mb-2 text-sm">
                  <span className="text-muted-foreground flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Payroll Package — cover sheet, register, tax & deduction summary, department costs, check register.</span>
                  <Button size="sm" variant="outline" data-testid="payroll-package-btn" disabled={pkgBusy} onClick={() => downloadPackage(detail)}>{pkgBusy ? "Generating…" : "Download package"}</Button>
                </div>
              )}
              {!["pending_approval", "rejected"].includes(detail.status) && <PaymentSplitCard run={detail} onChanged={() => openDetail(detail)} />}
              {!["pending_approval", "rejected"].includes(detail.status) && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 rounded-lg border bg-muted/40 px-3 py-2 mb-2 text-sm">
                  <span className="text-muted-foreground">{detail.payslips?.some((p) => p.check_number) ? `Checks printed: #${Math.min(...detail.payslips.filter((p) => p.check_number).map((p) => p.check_number))} – #${Math.max(...detail.payslips.filter((p) => p.check_number).map((p) => p.check_number))}` : "MICR checks not yet printed for this run."}</span>
                  <span className="flex gap-2">
                    {detail.payslips?.some((p) => p.check_number) && <Button size="sm" variant="outline" data-testid="reprint-checks-btn" disabled={printing} onClick={() => printChecks(detail, true)}>Reprint (new #s)</Button>}
                    <Button size="sm" data-testid="print-checks-btn" disabled={printing} onClick={() => printChecks(detail, false)}><Printer className="w-4 h-4 mr-1.5" /> {printing ? "Generating…" : detail.payslips?.some((p) => p.check_number) ? "Download checks" : "Print Checks"}</Button>
                  </span>
                </div>
              )}
              <div className="max-h-[45vh] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Tax</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                  <TableBody>{detail.payslips?.map((p) => (<TableRow key={p.id}><TableCell>{p.employee_name}{p.check_number && <span className="ml-2 text-[10px] font-mono-num text-muted-foreground">Chk #{p.check_number}</span>}</TableCell><TableCell className="text-right font-mono-num">{usd(p.gross_pay)}</TableCell><TableCell className="text-right font-mono-num">{usd(p.total_tax)}</TableCell><TableCell className="text-right font-mono-num font-semibold text-emerald-600">{usd(p.net_pay)}</TableCell></TableRow>))}</TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
